import { FC } from "react";
import { FieldInputProps } from "react-final-form";

import { IPickerListItem, Picker } from "@livechat/design-system-react-components";

import { PriorityPickerListItem } from "components/picker-items/priorityPickerItems";
import PriorityIndicator from "components/priority/PriorityIndicator";
import { TICKET_PRIORITY_LABEL_MAP } from "constants/tickets/priorities";
import { TicketPriority } from "interfaces/entities/ticket";
import { PickerContainerWithLabel } from "routes/automations/shared/components/PickerContainerWithLabel";
import { IRuleTriggerPriorityChanged } from "store/entities/rules/interfaces";

import { TRIGGER_ANY_OPTION_KEY, changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerPriorityChanged>;
}

export const PriorityChangedTrigger: FC<IProps> = ({ input }) => {
  const { oldPriorityIn, newPriorityIn } = input.value;

  const priorityOptions = [
    ANY_OPTION,
    ...[TicketPriority.Low, TicketPriority.Medium, TicketPriority.High, TicketPriority.Urgent].map(
      createPriorityOption
    ),
  ];

  const selectedFromPriorityOptions = oldPriorityIn ? oldPriorityIn.map(createPriorityOption) : [ANY_OPTION];

  const selectedToPriorityOptions = newPriorityIn ? newPriorityIn.map(createPriorityOption) : [ANY_OPTION];

  const handleSelectPriority =
    (property: "oldPriorityIn" | "newPriorityIn", previousSelected: IPickerListItem[]) =>
    (selectedOptions: IPickerListItem[] | null): void => {
      if (!selectedOptions) {
        changeTriggerValues(input, { [property]: [] });
        return;
      }

      const currentOptionKeys = selectedOptions.map(opt => opt.key);
      const previousOptionKeys = previousSelected.map(opt => opt.key);

      const isAnySelected = currentOptionKeys.includes(TRIGGER_ANY_OPTION_KEY);
      const isAnyPreviouslySelected = previousOptionKeys.includes(TRIGGER_ANY_OPTION_KEY);

      let newPriorityIn: TicketPriority[] | undefined;

      if (isAnySelected) {
        newPriorityIn = isAnyPreviouslySelected
          ? currentOptionKeys.filter(key => key !== TRIGGER_ANY_OPTION_KEY).map(key => parseInt(key, 10))
          : undefined;
      } else {
        newPriorityIn = currentOptionKeys.map(key => parseInt(key, 10));
      }

      changeTriggerValues(input, { [property]: newPriorityIn });
    };

  const handleSelectFromPriority = handleSelectPriority("oldPriorityIn", selectedFromPriorityOptions);

  const handleSelectToPriority = handleSelectPriority("newPriorityIn", selectedToPriorityOptions);

  return (
    <>
      <PickerContainerWithLabel label="from">
        <Picker
          type="multi"
          options={priorityOptions}
          selected={selectedFromPriorityOptions}
          onSelect={handleSelectFromPriority}
          searchDisabled
          placeholder="Select one priority or more…"
        />
      </PickerContainerWithLabel>
      <PickerContainerWithLabel label="to">
        <Picker
          type="multi"
          options={priorityOptions}
          selected={selectedToPriorityOptions}
          onSelect={handleSelectToPriority}
          searchDisabled
          placeholder="Select one priority or more…"
        />
      </PickerContainerWithLabel>
    </>
  );
};

const ANY_OPTION: IPickerListItem = {
  key: TRIGGER_ANY_OPTION_KEY,
  name: "Any priority",
};

const createPriorityOption = (priority: TicketPriority): IPickerListItem => ({
  key: priority.toString(),
  name: TICKET_PRIORITY_LABEL_MAP[priority],
  customElement: {
    listItemBody: <PriorityPickerListItem priority={priority} />,
  },
  selectedTagOptions: {
    leftNode: <PriorityIndicator priority={priority} />,
  },
});
